import React, { useMemo } from 'react';
import { useSelect } from 'downshift';
import { format, isThisYear, parseISO } from 'date-fns';
import {
  da,
  de,
  enAU,
  enCA,
  enGB,
  enUS,
  es,
  fi,
  fr,
  it,
  ja,
  ko,
  nb,
  nl,
  pl,
  pt,
  ptBR,
  sv,
  zhCN,
  zhTW,
} from 'date-fns/locale';
import { Icon } from 'js/components';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';

import styles from './BriefingVersionSelect.module.scss';
import sharedStyles from './OrgAnalyze.module.scss';

const DATE_FNS_LOCALES = {
  da,
  de,
  enAU,
  enCA,
  enGB,
  enUS,
  es,
  fi,
  fr,
  it,
  ja,
  ko,
  nb,
  nl,
  pl,
  pt,
  ptBR,
  sv,
  zhCN,
  zhTW,
};

function dateFnsLocaleKey(tag) {
  const [language, region] = String(tag || 'en-US').split('-');
  if (!region) {
    return language.toLowerCase();
  }
  return `${language.toLowerCase()}${region.toUpperCase()}`;
}

function dateFnsLocale() {
  const tag =
    typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US';
  const [language] = tag.split('-');
  return (
    DATE_FNS_LOCALES[dateFnsLocaleKey(tag)] ||
    DATE_FNS_LOCALES[language.toLowerCase()] ||
    enUS
  );
}

function formatWhen(iso) {
  if (!iso) {
    return '';
  }
  const date = parseISO(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const locale = dateFnsLocale();
  const day = format(date, isThisYear(date) ? 'MMM d' : 'MMM d, yyyy', {
    locale,
  });
  return `${day}, ${format(date, 'p', { locale })}`;
}

function isCurrentUser(item, currentUserId) {
  return (
    currentUserId != null &&
    item?.created_by_id != null &&
    String(item.created_by_id) === String(currentUserId)
  );
}

function versionName(item, currentUserId) {
  if (isCurrentUser(item, currentUserId)) {
    return 'You';
  }
  const name = item?.created_by_name?.trim();
  return name || 'Someone';
}

function itemToString(item, currentUserId) {
  if (!item) {
    return '';
  }
  const when = formatWhen(item.published_at);
  const name = versionName(item, currentUserId);
  return when ? `${name}, ${when}` : name;
}

function markerClass(visibility) {
  return visibility === 'draft'
    ? styles.versionMarkerDraft
    : styles.versionMarkerPublic;
}

function VersionMarker({ visibility }) {
  return (
    <span
      className={`${styles.versionMarker} ${markerClass(visibility)}`}
      aria-hidden="true"
    />
  );
}

function VersionLabel({ item, currentUserId }) {
  const yours = isCurrentUser(item, currentUserId);
  const name = versionName(item, currentUserId);
  const when = formatWhen(item?.published_at);

  return (
    <span className={styles.versionLabel}>
      <span
        className={`${styles.versionName} ${
          yours ? sharedStyles.versionYou : ''
        }`}
        title={name}
      >
        {name}
      </span>
      {when ? <span className={styles.versionWhen}>{when}</span> : null}
    </span>
  );
}

export default function BriefingVersionSelect({
  versions = [],
  value,
  onChange,
  currentUserId,
  disabled = false,
}) {
  const items = useMemo(() => versions, [versions]);
  const selectedItem =
    items.find((item) => item.id === value) ?? items[0] ?? null;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    items,
    selectedItem,
    itemToString: (item) => itemToString(item, currentUserId),
    onSelectedItemChange: ({ selectedItem: next }) => {
      if (next) {
        onChange(next.id);
      }
    },
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.versionSelect}>
      <button
        type="button"
        className={styles.versionToggle}
        disabled={disabled}
        {...getToggleButtonProps({
          disabled,
          'aria-label': 'Briefing version',
        })}
      >
        <VersionMarker visibility={selectedItem?.visibility} />
        <VersionLabel item={selectedItem} currentUserId={currentUserId} />
        <Icon icon={isOpen ? faChevronUp : faChevronDown} />
      </button>
      <ul {...getMenuProps()} className={styles.versionMenu} hidden={!isOpen}>
        {isOpen
          ? items.map((item, index) => (
              <li
                key={item.id}
                className={`${styles.versionItem} ${
                  highlightedIndex === index
                    ? styles.versionItemHighlighted
                    : ''
                } ${
                  selectedItem?.id === item.id ? styles.versionItemSelected : ''
                } ${item.official ? styles.versionItemOfficial : ''}`}
                {...getItemProps({ item, index })}
              >
                <VersionMarker visibility={item.visibility} />
                <VersionLabel item={item} currentUserId={currentUserId} />
              </li>
            ))
          : null}
        {isOpen ? (
          <li className={styles.versionLegend} aria-hidden="true">
            <span>
              <span
                className={`${styles.versionMarker} ${styles.versionMarkerPublic}`}
              />
              Public
            </span>
            <span>
              <span
                className={`${styles.versionMarker} ${styles.versionMarkerDraft}`}
              />
              Draft
            </span>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
